import { queryOptions } from '@tanstack/react-query'
import { CustomQueryOptions } from '@/common/types_and_interfaces.ts'
import {
  ProgramDetails,
  GetProgramDetail,
  GetProgramUsers,
  ProgramUsersList,
  GetUserPrograms,
  UserProgramList,
  UserRoles,
  V2UpdateProgram,
  V2UpdateProgramPayload,
  getGlobalPrograms,
} from './requests.ts'
import { z } from 'zod'
import { GlobalProgramsResponseProps } from './schemas.ts'

export const ProgramDetailsSchema = z.object({
  name: z.string({ message: 'Program name is required' }),
  global_state_id: z.number().optional(),
  enrollment_start_date: z
    .date({ message: 'Enrollment Start Date is required' })
    .optional(),
  enrollment_end_date: z
    .date({ message: 'Enrollment End Date is required' })
    .optional(),
  program_start_date: z
    .date({ message: ' Program Start Date is required' })
    .optional(),
  program_end_date: z
    .date({ message: 'Program End Date is required' })
    .optional(),
  manager_id: z.number({ message: 'Program Manager is required' }).optional(),
  created_by: z.number().optional(),
  created_datetime: z.date().optional(),
  verify_income: z.number().nullable(),
  class_wallet: z.number().nullable().optional(),
  unrulr: z.number().nullable().optional(),
})
export type ProgramDetailsFormPros = z.infer<typeof ProgramDetailsSchema>

const ProgramQueryKeys = {
  details: (program_id?: number) =>
    program_id ? ['program_detail', program_id] : ['program_detail', 'invalid'],
}

const ProgramUsersKeys = {
  details: (program_id: number) => ['users_list', program_id],
}

const UserProgramsKeys = {
  programsByUser: (user_id: number) => ['user_program_list', user_id],
}

export class ProgramQueries {
  public static getProgramById(
    program_id?: number,
    options: CustomQueryOptions<ProgramDetails> = {}
  ) {
    return {
      queryKey: ProgramQueryKeys.details(program_id),
      queryFn: program_id ? () => GetProgramDetail(program_id) : undefined,
      enabled: !!program_id,
      ...options,
    }
  }

  public static FetchProgramUsers(
    program_id?: number,
    options: CustomQueryOptions<ProgramUsersList> = {},
    roles?: UserRoles[]
  ) {
    return queryOptions({
      queryKey: ProgramUsersKeys.details(program_id!),
      queryFn: () => GetProgramUsers(program_id, roles),
      ...options,
    })
  }

  public static FetchUserPrograms(
    user_id?: number,
    options: CustomQueryOptions<UserProgramList> = {}
  ) {
    return queryOptions({
      queryKey: UserProgramsKeys.programsByUser(user_id!),
      queryFn: () => GetUserPrograms(user_id),
      ...options,
    })
  }

  public static V2UpdateProgramQuery(
    programId: number,
    requestBody: V2UpdateProgramPayload
  ) {
    return queryOptions({
      queryKey: ProgramQueryKeys.details(programId),
      queryFn: () => V2UpdateProgram(programId, requestBody),
    })
  }

  public static FetchGlobalPrograms(
    options: CustomQueryOptions<GlobalProgramsResponseProps> = {}
  ) {
    return queryOptions({
      queryKey: ['global_programs'],
      queryFn: () => getGlobalPrograms(),
      ...options,
    })
  }
}
